import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import {
  TrendingUp, CheckCircle2, AlertTriangle, Target, Activity, BarChart3,
  Users, ClipboardList, Award, ArrowRight, FileText, Sparkles, Heart, Eye,
  Phone, Mail, Calendar, Send, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useAppMode } from '../../contexts/AppModeContext';

const MIPS_SUMMARY = {
  qualityCompleted: 34,
  qualityPending: 12,
  improvementActivities: 3,
  promotingInterop: 2,
  projectedScore: 86,
  maxScore: 100,
};

const QUALITY_MEASURES = [
  { id: 'QM-001', name: 'Diabetes: HbA1c Poor Control', category: 'Diabetes', patients: 18, completed: 14, pending: 4, status: 'on_track' },
  { id: 'QM-002', name: 'Controlling High Blood Pressure', category: 'Hypertension', patients: 32, completed: 28, pending: 4, status: 'on_track' },
  { id: 'QM-003', name: 'Preventive Care: BMI Screening', category: 'Preventive', patients: 45, completed: 38, pending: 7, status: 'attention' },
  { id: 'QM-004', name: 'Tobacco Use Screening', category: 'Preventive', patients: 45, completed: 40, pending: 5, status: 'on_track' },
  { id: 'QM-005', name: 'Depression Screening', category: 'Behavioral', patients: 45, completed: 30, pending: 15, status: 'at_risk' },
  { id: 'QM-006', name: 'Colorectal Cancer Screening', category: 'Cancer', patients: 22, completed: 16, pending: 6, status: 'attention' },
];

const MIPS_OPTIMIZATION = [
  { id: 1, patient: 'John Smith', patientId: 'P001', measure: 'Diabetes HbA1c Control', missing: ['HbA1c result within 12 months', 'Medication reconciliation'], status: 'incomplete' },
  { id: 2, patient: 'Emily Davis', patientId: 'P004', measure: 'Blood Pressure Screening', missing: ['Blood pressure value documented', 'Smoking status'], status: 'incomplete' },
  { id: 3, patient: 'Robert Johnson', patientId: 'P003', measure: 'Depression Screening', missing: ['PHQ-9 score documented'], status: 'incomplete' },
];

const POPULATION_HEALTH = [
  { metric: 'Patients overdue for HbA1c test', count: 27, total: 120, icon: Activity, color: 'text-red-500' },
  { metric: 'Patients overdue for colonoscopy', count: 14, total: 85, icon: ClipboardList, color: 'text-amber-500' },
  { metric: 'Patients overdue for mammogram', count: 19, total: 72, icon: Heart, color: 'text-rose-500' },
  { metric: 'Patients overdue for eye exam (diabetic)', count: 11, total: 45, icon: Eye, color: 'text-blue-500' },
  { metric: 'Patients with uncontrolled A1c (>9%)', count: 8, total: 45, icon: AlertTriangle, color: 'text-red-500' },
  { metric: 'Patients needing flu vaccine', count: 34, total: 200, icon: Users, color: 'text-teal-500' },
];

const POPULATION_PATIENTS = {
  'Patients overdue for HbA1c test': [
    { id: 'P001', name: 'John Smith', age: 60, lastTest: '2025-06-15', daysOverdue: 245, phone: '(555) 100-1001', risk: 'high' },
    { id: 'P004', name: 'Emily Davis', age: 55, lastTest: '2025-07-20', daysOverdue: 210, phone: '(555) 100-1004', risk: 'medium' },
    { id: 'P006', name: 'David Wilson', age: 68, lastTest: '2025-05-10', daysOverdue: 281, phone: '(555) 100-1006', risk: 'high' },
    { id: 'P007', name: 'Lisa Anderson', age: 52, lastTest: '2025-08-02', daysOverdue: 197, phone: '(555) 100-1007', risk: 'low' },
    { id: 'P008', name: 'James Taylor', age: 71, lastTest: '2025-04-18', daysOverdue: 303, phone: '(555) 100-1008', risk: 'high' },
  ],
  'Patients overdue for colonoscopy': [
    { id: 'P002', name: 'Maria Garcia', age: 52, lastTest: '2024-08-10', daysOverdue: 554, phone: '(555) 100-1002', risk: 'medium' },
    { id: 'P009', name: 'Patricia Moore', age: 58, lastTest: '2024-06-20', daysOverdue: 605, phone: '(555) 100-1009', risk: 'high' },
    { id: 'P010', name: 'Thomas Jackson', age: 61, lastTest: '2024-09-15', daysOverdue: 518, phone: '(555) 100-1010', risk: 'medium' },
  ],
  'Patients overdue for mammogram': [
    { id: 'P004', name: 'Emily Davis', age: 55, lastTest: '2025-01-10', daysOverdue: 400, phone: '(555) 100-1004', risk: 'medium' },
    { id: 'P011', name: 'Jennifer White', age: 48, lastTest: '2024-11-20', daysOverdue: 451, phone: '(555) 100-1011', risk: 'high' },
    { id: 'P012', name: 'Nancy Harris', age: 63, lastTest: '2025-03-05', daysOverdue: 346, phone: '(555) 100-1012', risk: 'low' },
  ],
  'Patients overdue for eye exam (diabetic)': [
    { id: 'P001', name: 'John Smith', age: 60, lastTest: '2025-03-22', daysOverdue: 329, phone: '(555) 100-1001', risk: 'high' },
    { id: 'P006', name: 'David Wilson', age: 68, lastTest: '2025-01-15', daysOverdue: 395, phone: '(555) 100-1006', risk: 'high' },
  ],
  'Patients with uncontrolled A1c (>9%)': [
    { id: 'P001', name: 'John Smith', age: 60, lastTest: '2025-06-15', daysOverdue: 245, phone: '(555) 100-1001', risk: 'high', a1c: '9.2%' },
    { id: 'P006', name: 'David Wilson', age: 68, lastTest: '2025-05-10', daysOverdue: 281, phone: '(555) 100-1006', risk: 'high', a1c: '10.1%' },
    { id: 'P008', name: 'James Taylor', age: 71, lastTest: '2025-04-18', daysOverdue: 303, phone: '(555) 100-1008', risk: 'high', a1c: '9.8%' },
  ],
  'Patients needing flu vaccine': [
    { id: 'P002', name: 'Maria Garcia', age: 52, lastTest: 'Never', daysOverdue: 0, phone: '(555) 100-1002', risk: 'low' },
    { id: 'P003', name: 'Robert Johnson', age: 45, lastTest: '2025-01-20', daysOverdue: 390, phone: '(555) 100-1003', risk: 'low' },
    { id: 'P005', name: 'Sarah Williams', age: 38, lastTest: '2024-10-15', daysOverdue: 487, phone: '(555) 100-1005', risk: 'low' },
    { id: 'P009', name: 'Patricia Moore', age: 58, lastTest: '2025-02-10', daysOverdue: 369, phone: '(555) 100-1009', risk: 'medium' },
  ],
};

const LuminaMIPS = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedPatients, setSelectedPatients] = useState([]);

  const scoreColor = MIPS_SUMMARY.projectedScore >= 80 ? 'text-violet-600' : MIPS_SUMMARY.projectedScore >= 60 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="space-y-6" data-testid="mips-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">MIPS Dashboard</h1>
          <p className="text-slate-500 mt-1">Merit-based Incentive Payment System performance tracking</p>
        </div>
        <Badge className="bg-blue-50 text-blue-700 gap-1"><Award className="h-3 w-3" /> CMS Reporting</Badge>
      </div>

      {/* Score Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-violet-200 bg-violet-50/30 md:col-span-1">
          <CardContent className="p-5 text-center">
            <Target className="h-6 w-6 text-violet-500 mx-auto mb-2" />
            <p className={cn("text-3xl font-bold", scoreColor)}>{MIPS_SUMMARY.projectedScore}</p>
            <p className="text-[11px] text-muted-foreground mt-1">Projected Score</p>
            <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-violet-500 rounded-full" style={{ width: `${MIPS_SUMMARY.projectedScore}%` }} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5 text-center">
            <CheckCircle2 className="h-5 w-5 text-emerald-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{MIPS_SUMMARY.qualityCompleted}</p>
            <p className="text-[11px] text-muted-foreground mt-1">Measures Completed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5 text-center">
            <AlertTriangle className="h-5 w-5 text-amber-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{MIPS_SUMMARY.qualityPending}</p>
            <p className="text-[11px] text-muted-foreground mt-1">Measures Pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5 text-center">
            <Activity className="h-5 w-5 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{MIPS_SUMMARY.improvementActivities}</p>
            <p className="text-[11px] text-muted-foreground mt-1">Improvement Activities</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5 text-center">
            <BarChart3 className="h-5 w-5 text-slate-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{MIPS_SUMMARY.promotingInterop}</p>
            <p className="text-[11px] text-muted-foreground mt-1">Promoting Interop</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Quality Measures</TabsTrigger>
          <TabsTrigger value="optimization">Optimization</TabsTrigger>
          <TabsTrigger value="population">Population Health</TabsTrigger>
        </TabsList>

        {/* Quality Measures */}
        <TabsContent value="overview" className="mt-4">
          <Card data-testid="quality-measures-table">
            <CardHeader>
              <CardTitle className="text-base font-semibold">Quality Measures</CardTitle>
              <CardDescription>Track performance across CMS quality measures</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Measure</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-center">Patients</TableHead>
                    <TableHead className="text-center">Completed</TableHead>
                    <TableHead className="text-center">Pending</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {QUALITY_MEASURES.map(m => {
                    const pct = Math.round((m.completed / m.patients) * 100);
                    return (
                      <TableRow key={m.id}>
                        <TableCell className="font-medium text-sm">{m.name}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{m.category}</Badge></TableCell>
                        <TableCell className="text-center">{m.patients}</TableCell>
                        <TableCell className="text-center font-medium">{m.completed}</TableCell>
                        <TableCell className="text-center text-muted-foreground">{m.pending}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                              <div className={cn("h-full rounded-full", pct >= 80 ? "bg-violet-500" : pct >= 60 ? "bg-amber-500" : "bg-red-500")} style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs text-muted-foreground">{pct}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn("text-xs",
                            m.status === 'on_track' ? 'bg-emerald-50 text-emerald-700' :
                            m.status === 'attention' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'
                          )}>
                            {m.status === 'on_track' ? 'On Track' : m.status === 'attention' ? 'Attention' : 'At Risk'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Optimization */}
        <TabsContent value="optimization" className="mt-4 space-y-4" data-testid="mips-optimization">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-600" /> MIPS Optimization Suggestions</CardTitle>
              <CardDescription>AI-detected documentation gaps that affect your MIPS score</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {MIPS_OPTIMIZATION.map(opt => (
                <div key={opt.id} className="flex items-start justify-between p-4 rounded-xl border border-amber-200 bg-amber-50/50" data-testid={`optimization-${opt.id}`}>
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold">{opt.patient} &mdash; {opt.measure}</p>
                      <p className="text-xs text-muted-foreground mt-1">Missing documentation:</p>
                      <ul className="mt-1 space-y-0.5">
                        {opt.missing.map((m, i) => (
                          <li key={i} className="text-xs text-amber-800 flex items-center gap-1">
                            <span className="h-1 w-1 rounded-full bg-amber-500" /> {m}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <Button size="sm" className="bg-black hover:bg-gray-800 gap-1 text-xs shrink-0" onClick={() => navigate(`/lumina/patients/${opt.patientId}`)}>
                    Update Chart <ArrowRight className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Population Health */}
        <TabsContent value="population" className="mt-4" data-testid="population-health">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-violet-600" /> Population Health Insights</CardTitle>
              <CardDescription>Click a patient group to view patients and take action</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {POPULATION_HEALTH.map((item, i) => {
                  const Icon = item.icon;
                  const pct = Math.round((item.count / item.total) * 100);
                  return (
                    <div
                      key={i}
                      className="flex items-center justify-between p-4 rounded-xl border hover:bg-slate-50 hover:border-slate-300 transition-all cursor-pointer"
                      onClick={() => { setSelectedGroup(item); setSelectedPatients([]); }}
                      data-testid={`population-group-${i}`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className={cn("h-5 w-5", item.color)} />
                        <div>
                          <p className="text-sm font-medium">{item.metric}</p>
                          <p className="text-xs text-muted-foreground">{item.count} of {item.total} patients ({pct}%)</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-bold text-slate-900">{item.count}</p>
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Population Health Lightbox ───────────────────── */}
      <Dialog open={!!selectedGroup} onOpenChange={(open) => { if (!open) setSelectedGroup(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="population-lightbox">
          {selectedGroup && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {(() => { const Icon = selectedGroup.icon; return <Icon className={cn("h-5 w-5", selectedGroup.color)} />; })()}
                  {selectedGroup.metric}
                </DialogTitle>
                <DialogDescription>
                  {selectedGroup.count} of {selectedGroup.total} patients &middot; Click actions to reach out or schedule
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-2">
                {/* Select All */}
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300"
                      checked={selectedPatients.length === (POPULATION_PATIENTS[selectedGroup.metric] || []).length}
                      onChange={e => {
                        const all = (POPULATION_PATIENTS[selectedGroup.metric] || []).map(p => p.id);
                        setSelectedPatients(e.target.checked ? all : []);
                      }}
                    />
                    <span className="font-medium">Select All</span>
                  </label>
                  {selectedPatients.length > 0 && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="gap-1 text-xs h-7" onClick={() => { toast.success(`Reminder sent to ${selectedPatients.length} patients`); setSelectedPatients([]); }} data-testid="bulk-remind-btn">
                        <Mail className="h-3 w-3" /> Send Reminder ({selectedPatients.length})
                      </Button>
                      <Button size="sm" className="bg-black hover:bg-gray-800 gap-1 text-xs h-7" onClick={() => { toast.success(`${selectedPatients.length} appointments scheduled`); setSelectedPatients([]); }} data-testid="bulk-schedule-btn">
                        <Calendar className="h-3 w-3" /> Schedule All ({selectedPatients.length})
                      </Button>
                    </div>
                  )}
                </div>

                {/* Patient List */}
                {(POPULATION_PATIENTS[selectedGroup.metric] || []).map(patient => {
                  const isSelected = selectedPatients.includes(patient.id);
                  return (
                    <div key={patient.id} className={cn("flex items-center justify-between p-3 rounded-xl border transition-colors", isSelected ? "bg-slate-50 border-slate-300" : "hover:bg-slate-50")} data-testid={`pop-patient-${patient.id}`}>
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300"
                          checked={isSelected}
                          onChange={() => setSelectedPatients(prev => isSelected ? prev.filter(id => id !== patient.id) : [...prev, patient.id])}
                        />
                        <div>
                          <p className="text-sm font-medium">{patient.name}</p>
                          <p className="text-xs text-muted-foreground">
                            Age {patient.age} &middot; Last: {patient.lastTest}
                            {patient.daysOverdue > 0 && ` (${patient.daysOverdue} days overdue)`}
                            {patient.a1c && ` &middot; A1c: ${patient.a1c}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={cn("text-[10px]",
                          patient.risk === 'high' ? 'bg-red-50 text-red-700' :
                          patient.risk === 'medium' ? 'bg-amber-50 text-amber-700' :
                          'bg-slate-100 text-slate-600'
                        )}>{patient.risk}</Badge>
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => { toast.success(`Reminder sent to ${patient.name}`); }} data-testid={`remind-${patient.id}`}>
                          <Phone className="h-3 w-3" /> Remind
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => navigate(`/lumina/patients/${patient.id}`)} data-testid={`view-chart-${patient.id}`}>
                          <FileText className="h-3 w-3" /> Chart
                        </Button>
                        <Button size="sm" className="bg-black hover:bg-gray-800 h-7 text-xs gap-1" onClick={() => { toast.success(`Appointment scheduled for ${patient.name}`); }} data-testid={`schedule-${patient.id}`}>
                          <Calendar className="h-3 w-3" /> Schedule
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <DialogFooter className="border-t pt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mr-auto">
                  <Sparkles className="h-3.5 w-3.5 text-violet-500" />
                  AI-generated outreach recommendations based on risk level and overdue duration
                </div>
                <Button variant="outline" onClick={() => setSelectedGroup(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LuminaMIPS;
